<template>
    <div :id="id" class="date-picker">
        <input-button
            class="rounded" :type="isValidDate ? 'outline' : 'dashed'"
            :icon="icon" @click="showDatePicker" @click-inner="didClickAuxiliary"
        >
            <template v-if="isValidDate">
                <span>
                    {{ journeyTimeDate(value, true) }}
                </span>
                <span class="d-none d-sm-inline-block">
                    {{ value.after ? 'departing' : 'arriving' }}
                </span>
                <span class="d-inline-block d-sm-none">
                    {{ value.after ? 'depart' : 'arrive' }}
                </span>
                <span>
                    {{ value.after ? ' after' : ' before' }}
                </span>
                <span>
                    {{ value.time }} <font-awesome-icon icon="fa-regular fa-calendar" class="ml-1"></font-awesome-icon>
                </span>
            </template>
            <slot v-else></slot>
        </input-button>
        <div v-if="isOpen" class="block-ui" @click="closePicker"></div>
        <div v-show="isOpen" class="background-blocker show-when-modal"></div>
        <div v-show="isOpen" class="dropdown date-picker-dropdown rounded shadow">
            <div class="dropdown-header show-when-modal">
                <slot></slot>
            </div>
            <div class="date-picker"></div>
            <div class="time-selections">
                <select v-model="after" class="departure">
                    <option value="1">
                        Depart After
                    </option>
                    <option value="0">
                        Arrive Before
                    </option>
                </select>
                <select v-model="time.hours" class="hours">
                    <option v-for="hour in hourList" :key="hour">
                        {{ hour }}
                    </option>
                </select>
                <div class="spacer">
                    :
                </div>
                <select v-model="time.minutes" class="minutes">
                    <option v-for="minute in minuteList" :key="minute">
                        {{ minute }}
                    </option>
                </select>
            </div>
            <div
                v-if="applicableDisruptionsArray.length > 0"
                v-show="isOpen"
                class="disruption-warning alert alert-warning"
                role="alert"
            >
                <p>
                    <strong>Disruption on {{ disruptionDate }}:</strong>
                    <template v-for="(disruption, index) in applicableDisruptionsArray">
                        {{ disruption }}<template v-if="index < applicableDisruptionsArray.length - 1">, </template>
                    </template>
                </p>
            </div>
            <div class="action-buttons">
                <button class="btn btn-primary" @click="closePicker">
                    Done
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { DateTime } from 'luxon';
    import { FormatUtils } from '@raileasyuk/ts-frontend-utils';
    import InputButton from './InputButton';
    import { DisruptionUtils } from '../utilities/DisruptionUtils';

    export default {
        name: 'DatePicker',
        components: {
            InputButton,
        },
        props: {
            id: {
                type: String,
                default: 'date-picker',
            },
            descriptor: {
                type: String,
                default: '',
            },
            removable: {
                type: Boolean,
                default: false,
            },
            disruptions: {
                type: Array,
                default: () => [],
                required: false,
            },
            value: {
                type: Object,
                default() {
                    return {
                        date: null,
                        time: null,
                        after: true,
                    };
                },
            },
            min: {
                type: String,
                default: null,
            },
            max: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                isOpen: false,
                date: null,
                time: {
                    hours: null,
                    minutes: null,
                },
                applicableDisruptions: [],
                disruptionDate: null,
                after: '1',
                picker: null,
            };
        },
        computed: {
            isValidDate() {
                return this.value.date !== null;
            },
            icon() {
                const baseClassName = this.isValidDate ? 'remove' : 'add';
                return this.removable ? baseClassName : 'none';
            },
            hourList() {
                return this.generateList(0, 23, 1);
            },
            minuteList() {
                return this.generateList(0, 59, 15);
            },
            groupedDisruptions() {
                return DisruptionUtils.getDisruptions(this.disruptions);
            },
            applicableDisruptionsArray() {
                if (this.applicableDisruptions.length > 0) {
                    return this.applicableDisruptions.map((disruption) => this.formatDisruptionSummary(disruption));
                }
                return [];
            },
        },
        watch: {
            after() {
                this.submitDate();
            },
            // eslint-disable-next-line func-names
            'time.hours': function () {
                this.submitDate();
            },
            // eslint-disable-next-line func-names
            'time.minutes': function () {
                this.submitDate();
            },
        },
        mounted() {
            this.picker = $(`#${this.id} .date-picker`).datepicker({
                dayNamesMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                dateFormat: 'yy-mm-dd',
                firstDay: 1,
                onSelect: (dateText) => {
                    this.applicableDisruptions = this.getApplicableDisruptions(DateTime.fromISO(dateText).toJSDate());
                    this.disruptionDate = FormatUtils.dayFormatFromDate(DateTime.fromJSDate(this.picker.datepicker('getDate')));
                    this.submitDate();
                },
                numberOfMonths: window.matchMedia(
                    this.removable ? '(max-width: 1150px), (max-height: 950px)'
                        : '(max-width: 768px), (max-height: 950px)',
                ).matches ? 1 : 2,
                beforeShowDay: (date) => {
                    const dayOfMonth = date.getDate();
                    let applicableDisruptions = [];
                    if (this.$props.disruptions) {
                        applicableDisruptions = this.getApplicableDisruptions(date);
                    }
                    if (applicableDisruptions.length > 0) {
                        const temp = applicableDisruptions.map((disruption) => this.formatDisruptionSummary(disruption));
                        return [true, 'disruption', temp.join(', ')];
                    }
                    return [dayOfMonth !== 25 || date.getMonth() !== 11, ''];
                },
            });
            this.applicableDisruptions = this.getApplicableDisruptions(this.picker.datepicker('getDate'));
            this.disruptionDate = FormatUtils.dayFormatFromDate(DateTime.fromJSDate(this.picker.datepicker('getDate')));
        },
        methods: {
            formatDisruptionSummary(disruption) {
                const operators = disruption.tocs.filter((toc) => toc !== '' && toc !== null);
                const uniqueOperators = [...new Set(operators)];
                let operatorText = '';

                if (uniqueOperators.length > 3) {
                    operatorText = `${uniqueOperators.length} operators affected`;
                } else if (uniqueOperators.length > 0) {
                    operatorText = uniqueOperators.join(', ');
                }

                return operatorText
                  ? `${disruption.descriptions.join(', ')} (${operatorText})`
                  : disruption.descriptions.join(', ');
            },
            getApplicableDisruptions(date) {
                return this.groupedDisruptions.filter((d) => {
                    const start = new Date(d.startDateLocal);
                    start.setHours(0, 0, 0);
                    const end = new Date(d.endDateLocal);
                    end.setHours(23, 59, 59);

                    return (date.getTime() >= start.getTime() && date.getTime() <= end.getTime());
                });
            },
            invalidateDate() {
                this.$emit('input', {
                    date: null,
                    time: null,
                    after: true,
                });
            },
            didClickAuxiliary() {
                if (this.removable && this.isValidDate) {
                    this.invalidateDate();
                } else {
                    this.showDatePicker();
                }
            },
            showDatePicker() {
                const splitTime = this.value.time ? this.value.time.split(':') : null;
                const nowTime = DateTime.local().plus({ minutes: 30 }).setZone('Europe/London');
                const hours = splitTime ? splitTime[0] : nowTime.hour;
                const minutes = splitTime ? splitTime[1] : nowTime.minute;
                if (!this.time.hours) {
                    this.time.hours = this.getClosest(this.hourList, hours);
                }
                if (!this.time.minutes) {
                    this.time.minutes = this.getClosest(this.minuteList, minutes);
                }
                if (this.picker) {
                  const nowUtc = DateTime.utc();
                    this.picker.datepicker('option', 'minDate', this.min ? new Date(this.min)
                        : new Date(nowUtc.year, nowUtc.month - 1, nowUtc.day));
                    this.picker.datepicker('option', 'maxDate', this.max ? new Date(this.max) : '+3M');
                    if (this.value.date) {
                        this.picker.datepicker('setDate', this.value.date);
                    } else {
                        this.picker.datepicker('setDate', DateTime.local(nowTime.year, nowTime.month, nowTime.day).toJSDate());
                    }
                }
                this.after = this.value.after ? '1' : '0';
                this.isOpen = true;
            },
            journeyTimeDate(journeyTime, emoji) {
                const disruptions = this.getApplicableDisruptions(DateTime.fromISO(journeyTime.date).toJSDate());
                if (disruptions.length > 0 && emoji) {
                    return `⚠️ ${FormatUtils.dayFormatFromDate(DateTime.fromISO(journeyTime.date))}`;
                }
                return FormatUtils.dayFormatFromDate(DateTime.fromISO(journeyTime.date));
            },
            closePicker() {
                this.isOpen = false;
            },
            submitDate() {
                this.$emit('input', {
                    date: this.picker ? $.datepicker.formatDate('yy-mm-dd', this.picker.datepicker('getDate')) : null,
                    time: `${this.time.hours}:${this.time.minutes}`,
                    after: parseInt(this.after, 10) === 1,
                });
            },
            generateList(from, to, step) {
                const list = [];
                for (let x = from; x <= to; x += step) {
                    list.push(`0${x}`.slice(-2));
                }
                return list;
            },
            getClosest(list, value) {
                for (let x = 0; x < list.length; x++) {
                    if (parseInt(list[x], 10) >= parseInt(value, 10)) {
                        return list[x];
                    }
                }
                return list[list.length - 1];
            },
        },
    };
</script>
